import React, { useCallback, useMemo, useState } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { useTranslation } from '../i18n/useTranslation';
import { isRadioQueueSession } from '../radio/radioTrackRegistry';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TrackSourceBadge from './queue/TrackSourceBadge';

const GOLD = '#C9A84C';

const fmt = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
};

function SortableQueueRow({ id, globalIndex, queueIndex, track, onPlay, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <QueueRow
        globalIndex={globalIndex}
        queueIndex={queueIndex}
        track={track}
        onPlay={onPlay}
        onRemove={onRemove}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

function QueueRow({ globalIndex, queueIndex, track, onPlay, onRemove, dragHandleProps }) {
  const isCurrent = globalIndex === queueIndex;
  const isPast = globalIndex < queueIndex;

  return (
    <div
      onClick={() => onPlay(globalIndex)}
      className="flex items-center px-5 py-2.5 cursor-pointer transition group"
      style={{
        background: isCurrent ? 'rgba(201,168,76,0.08)' : 'transparent',
        opacity: isPast ? 0.4 : 1,
        borderLeft: isCurrent ? `3px solid ${GOLD}` : '3px solid transparent',
      }}
    >
      {/* Drag handle — only for upcoming tracks that have drag props */}
      {dragHandleProps ? (
        <div
          {...dragHandleProps}
          className="flex-shrink-0 mr-2 cursor-grab active:cursor-grabbing touch-manipulation"
          style={{ color: 'var(--text-tertiary)', opacity: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </div>
      ) : (
        <div className="flex-shrink-0 mr-2" style={{ width: 16 }} />
      )}

      {/* Thumbnail */}
      <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden mr-3" style={{ background: 'var(--bg-secondary)' }}>
        {track?.artworkUrl ? (
          <img src={track.artworkUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: 'var(--text-tertiary)', fontSize: 18 }}>♪</div>
        )}
      </div>

      {/* Track info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: isCurrent ? GOLD : 'var(--text-primary)' }}>
          {track?.title || 'Unknown Track'}
        </p>
        <p className="text-xs truncate mt-0.5 flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
          <span className="truncate">{track?.artist || track?.artistName || ''}</span>
          <TrackSourceBadge track={track} unavailable={track?._unavailable === true} />
        </p>
      </div>

      {/* Playing indicator for current */}
      {isCurrent && (
        <div className="flex-shrink-0 mr-3">
          <div className="flex items-end gap-0.5 h-4">
            <span className="w-0.5 bg-[#C9A84C] rounded-full" style={{ height: '60%', animation: 'vuBounce 0.6s ease-in-out infinite' }} />
            <span className="w-0.5 bg-[#C9A84C] rounded-full" style={{ height: '100%', animation: 'vuBounce 0.6s ease-in-out infinite 0.15s' }} />
            <span className="w-0.5 bg-[#C9A84C] rounded-full" style={{ height: '40%', animation: 'vuBounce 0.6s ease-in-out infinite 0.3s' }} />
          </div>
        </div>
      )}

      {/* Duration */}
      <span className="flex-shrink-0 text-xs tabular-nums mr-2" style={{ color: 'var(--text-tertiary)' }}>
        {track?.duration ? fmt(track.duration) : ''}
      </span>

      {/* Remove button — shown for past and upcoming tracks, not the current one */}
      {!isCurrent && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(globalIndex); }}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition touch-manipulation"
          style={{ background: 'var(--bg-secondary)' }}
          title="Remove from queue"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function QueuePanel({ isOpen, onClose, onPlayIndex }) {
  const { t } = useTranslation();
  const queue = useMaestroStore((s) => s.queue);
  const queueIndex = useMaestroStore((s) => s.queueIndex);
  const activeSource = useMaestroStore((s) => s.activeSource);
  const removeFromQueue = useMaestroStore((s) => s.removeFromQueue);
  const reorderQueue = useMaestroStore((s) => s.reorderQueue);
  const clearUpcoming = useMaestroStore((s) => s.clearUpcoming);
  const [activeDragId, setActiveDragId] = useState(null);

  const isRadioQueue = isRadioQueueSession() || activeSource === 'radio';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const pastAndCurrent = useMemo(() =>
    queue.slice(0, queueIndex + 1).map((item, i) => ({ item, globalIndex: i })),
  [queue, queueIndex]);

  const upcoming = useMemo(() =>
    queue.slice(queueIndex + 1).map((item, i) => ({ item, globalIndex: queueIndex + 1 + i })),
  [queue, queueIndex]);

  const upcomingSortableIds = useMemo(() =>
    upcoming.map((_, i) => `uq-${i}`),
  [upcoming]);

  const upcomingCount = upcoming.length;
  const totalRemaining = useMemo(() => {
    let total = 0;
    for (let i = queueIndex; i < queue.length; i++) {
      total += queue[i]?.track?.duration || 0;
    }
    return total;
  }, [queue, queueIndex]);

  const formatTotal = (secs) => {
    if (!secs) return '';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handlePlay = useCallback((globalIndex) => {
    onPlayIndex?.(globalIndex);
    onClose?.();
  }, [onPlayIndex, onClose]);

  const handleRemove = useCallback((globalIndex) => {
    removeFromQueue(globalIndex);
  }, [removeFromQueue]);

  const handleClear = useCallback(() => {
    clearUpcoming();
  }, [clearUpcoming]);

  const handleShuffleUpcoming = useCallback(async () => {
    const { QueueController } = await import('../transport/QueueController');
    QueueController.shuffleUpcoming();
  }, []);

  const handleClearAll = useCallback(async () => {
    if (queue.length === 0) return;
    const msg = queue.length > 10
      ? `Clear all ${queue.length} tracks from the queue?`
      : 'Clear the entire queue?';
    if (!window.confirm(msg)) return;
    const { QueueController } = await import('../transport/QueueController');
    await QueueController.clearQueue();
  }, [queue.length]);

  const handleDragStart = useCallback((event) => {
    setActiveDragId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromLocal = upcomingSortableIds.indexOf(active.id);
    const toLocal = upcomingSortableIds.indexOf(over.id);
    if (fromLocal < 0 || toLocal < 0) return;
    const fromGlobal = queueIndex + 1 + fromLocal;
    const toGlobal = queueIndex + 1 + toLocal;
    reorderQueue(fromGlobal, toGlobal);
  }, [upcomingSortableIds, queueIndex, reorderQueue]);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  if (!isOpen) return null;

  const activeDragLocalIndex = activeDragId ? upcomingSortableIds.indexOf(activeDragId) : -1;
  const activeDragEntry = activeDragLocalIndex >= 0 ? upcoming[activeDragLocalIndex] : null;
  const activeDragTrack = activeDragEntry ? (activeDragEntry.item?.track || activeDragEntry.item) : null;
  const activeDragGlobalIndex = activeDragEntry ? activeDragEntry.globalIndex : -1;

  return (
    <div className="fixed inset-0 z-[112]" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div
        className="absolute right-0 top-0 bottom-0 flex flex-col"
        style={{
          width: 420, maxWidth: '100vw',
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.3)',
          animation: 'queueSlideIn 0.25s ease both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', borderBottom: '1px solid var(--border)' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Queue</h2>
            {upcomingCount > 0 && (
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {upcomingCount} track{upcomingCount !== 1 ? 's' : ''} remaining
                {totalRemaining > 0 && ` (${formatTotal(Math.floor(totalRemaining))})`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {queue.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs font-medium px-3 py-1.5 rounded-lg transition hover:opacity-80"
                style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
              >
                Clear queue
              </button>
            )}
            {upcomingCount > 0 && (
              <button
                onClick={handleClear}
                className="text-xs font-medium px-3 py-1.5 rounded-lg transition hover:opacity-80"
                style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
              >
                Clear upcoming
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition hover:opacity-80"
              style={{ background: 'var(--bg-secondary)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable track list */}
        <div className="flex-1 overflow-y-auto no-scrollbar" style={{ paddingBottom: 100 }}>

          {/* Past + current tracks — plain rows, no sortable wrapper */}
          {pastAndCurrent.length > 0 && (
            <>
              <div className="px-5 pt-3 pb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Now Playing</span>
              </div>
              {pastAndCurrent.map(({ item, globalIndex }) => {
                const track = item?.track || item;
                return (
                  <QueueRow
                    key={`past-${globalIndex}`}
                    globalIndex={globalIndex}
                    queueIndex={queueIndex}
                    track={track}
                    onPlay={handlePlay}
                    onRemove={handleRemove}
                    dragHandleProps={null}
                  />
                );
              })}
            </>
          )}

          {/* Upcoming tracks — sortable with drag handles */}
          {upcoming.length > 0 && (
            <>
              <div className="px-5 pt-3 pb-1 flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Upcoming</span>
                <button
                  type="button"
                  onClick={handleShuffleUpcoming}
                  disabled={isRadioQueue || upcomingCount < 2}
                  className="flex items-center justify-center w-7 h-7 rounded-lg transition hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                  style={{
                    color: isRadioQueue ? 'var(--text-tertiary)' : GOLD,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                  }}
                  aria-label={t('queue_shuffle')}
                  title={isRadioQueue ? t('queue_shuffle_disabled_radio') : t('queue_shuffle')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 3 21 3 21 8" />
                    <line x1="4" y1="20" x2="21" y2="3" />
                    <polyline points="21 16 21 21 16 21" />
                    <line x1="15" y1="15" x2="21" y2="21" />
                    <line x1="4" y1="4" x2="9" y2="9" />
                  </svg>
                </button>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext items={upcomingSortableIds} strategy={verticalListSortingStrategy}>
                  {upcoming.map(({ item, globalIndex }, localIndex) => {
                    const track = item?.track || item;
                    return (
                      <SortableQueueRow
                        key={upcomingSortableIds[localIndex]}
                        id={upcomingSortableIds[localIndex]}
                        globalIndex={globalIndex}
                        queueIndex={queueIndex}
                        track={track}
                        onPlay={handlePlay}
                        onRemove={handleRemove}
                      />
                    );
                  })}
                </SortableContext>

                <DragOverlay dropAnimation={null}>
                  {activeDragTrack ? (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid var(--border)' }}>
                      <QueueRow
                        globalIndex={activeDragGlobalIndex}
                        queueIndex={queueIndex}
                        track={activeDragTrack}
                        onPlay={() => {}}
                        onRemove={() => {}}
                        dragHandleProps={null}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}

          {queue.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1" className="mb-4">
                <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
              </svg>
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Your queue is empty</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>Play an album or add tracks to get started</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes queueSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes vuBounce { 0%, 100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
      `}</style>
    </div>
  );
}
